import { useState } from "react";
import { Mail, Phone, MapPin, Clock3, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import Reveal from "@/components/site/Reveal";

const topics = [
  "Suchtprävention",
  "Persönliche Entwicklung",
  "Ängste und Unsicherheiten",
  "Partnerschaft und Beziehungen",
  "Achtsamkeit und innere Balance",
  "Sonstiges",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    topic: topics[0],
    message: "",
    consent: false,
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target?.type === "checkbox" ? e.target.checked : e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Bitte bestätige die Datenschutzhinweise.");
      return;
    }
    if (form.message.trim().length < 5) {
      toast.error("Bitte schreib mir eine kurze Nachricht.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message || "Danke! Deine Nachricht ist eingegangen.");
      setForm({ name: "", email: "", phone: "", topic: topics[0], message: "", consent: false });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Etwas ist schiefgelaufen. Bitte versuch es erneut.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="kontakt"
      data-testid="contact-section"
      className="relative py-24 sm:py-32"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left: text + contact details */}
          <Reveal className="lg:col-span-5">
            <div className="text-[11px] tracking-[0.3em] uppercase text-[#93401D] mb-5">
              Kontakt
            </div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-[3.5rem] tracking-tight leading-[1.02] text-[#29170C]">
              Schreib mir.
              <br />
              <span className="italic text-[#93401D]">Vertraulich, unverbindlich.</span>
            </h2>
            <p className="mt-6 text-[#63432B] leading-relaxed text-[17px] max-w-md">
              Ein erster Schritt fühlt sich oft groß an. Eine Nachricht zu
              schreiben hingegen ist klein – und ich melde mich persönlich bei
              dir zurück.
            </p>

            <div className="mt-10 space-y-5">
              <ContactRow icon={Mail} label="E-Mail" value="witalialbrecht21@web.de" />
              <ContactRow icon={Phone} label="Telefon" value="+49 177 1973430" />
              <ContactRow
                icon={Clock3}
                label="Telefonisch erreichbar"
                value="Mo–Fr 17–21 Uhr · Sa 8–18 Uhr · So & feiertags 10–14 Uhr"
              />
              <ContactRow icon={MapPin} label="Ort" value="Lünen · Beratung: Online und nach Vereinbarung persönlich" />
            </div>

            <div className="mt-12 rounded-2xl bg-[#EAD0AE]/70 border border-[#E0BD93]/60 p-6 max-w-md">
              <div className="text-[11px] tracking-[0.25em] uppercase text-[#93401D] mb-2">
                Im Notfall
              </div>
              <p className="text-sm text-[#63432B] leading-relaxed">
                Diese Beratung ersetzt keine ärztliche oder psychotherapeutische
                Behandlung. In akuten Krisen wende dich bitte an die
                Telefonseelsorge:{" "}
                <span className="font-medium text-[#29170C]">0800 111 0 111</span>{" "}
                (kostenfrei, rund um die Uhr). Bei akuter Gefahr rufe bitte den
                Notruf: <span className="font-medium text-[#29170C]">112</span>.
              </p>
            </div>
          </Reveal>

          {/* Right: form */}
          <Reveal delay={0.1} className="lg:col-span-7">
            <form
              data-testid="contact-form"
              onSubmit={onSubmit}
              className="bg-[#FCF4E8] border border-[#EAD0AE] rounded-[1.75rem] p-7 sm:p-10 shadow-[0_30px_60px_-40px_rgba(60,34,19,0.25)]"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Name" htmlFor="name">
                  <input
                    id="name"
                    data-testid="contact-input-name"
                    required
                    minLength={2}
                    value={form.name}
                    onChange={update("name")}
                    className="form-input"
                    placeholder="Dein Name"
                  />
                </Field>
                <Field label="E-Mail" htmlFor="email">
                  <input
                    id="email"
                    type="email"
                    data-testid="contact-input-email"
                    required
                    value={form.email}
                    onChange={update("email")}
                    className="form-input"
                    placeholder="deine@email.de"
                  />
                </Field>
                <Field label="Telefon (optional)" htmlFor="phone">
                  <input
                    id="phone"
                    data-testid="contact-input-phone"
                    value={form.phone}
                    onChange={update("phone")}
                    className="form-input"
                    placeholder="+49 ..."
                  />
                </Field>
                <Field label="Anliegen" htmlFor="topic">
                  <select
                    id="topic"
                    data-testid="contact-select-topic"
                    value={form.topic}
                    onChange={update("topic")}
                    className="form-input"
                  >
                    {topics.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>

              <Field label="Nachricht" htmlFor="message" className="mt-5">
                <textarea
                  id="message"
                  data-testid="contact-input-message"
                  required
                  rows={6}
                  value={form.message}
                  onChange={update("message")}
                  className="form-input resize-none"
                  placeholder="Worum geht es? Du kannst ganz offen schreiben – alles bleibt streng vertraulich."
                />
              </Field>

              <label className="mt-5 flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="contact-consent"
                  checked={form.consent}
                  onChange={update("consent")}
                  className="mt-1 w-5 h-5 shrink-0 accent-[#93401D]"
                />
                <span className="text-sm text-[#63432B] leading-relaxed">
                  Ich habe die{" "}
                  <a href="/datenschutz" className="underline underline-offset-2 text-[#93401D] hover:text-[#773117]">
                    Datenschutzhinweise
                  </a>{" "}
                  gelesen und stimme der Verarbeitung meiner Angaben zur Bearbeitung der Anfrage zu.
                </span>
              </label>

              <button
                type="submit"
                data-testid="contact-submit"
                disabled={loading}
                className="btn-primary mt-8 rounded-full px-8 py-3.5 text-sm font-medium inline-flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Wird gesendet…
                  </>
                ) : (
                  <>
                    Nachricht senden
                    <Send size={15} strokeWidth={1.8} />
                  </>
                )}
              </button>
            </form>
          </Reveal>
        </div>
      </div>

      <style>{`
        .form-input {
          width: 100%;
          background: #FFFFFF;
          border: 1px solid #E0BD93;
          border-radius: 14px;
          padding: 12px 16px;
          font-size: 15px;
          color: #29170C;
          font-family: 'Work Sans', sans-serif;
          transition: border-color 200ms ease, box-shadow 200ms ease;
          outline: none;
        }
        .form-input::placeholder { color: #937353; }
        .form-input:focus {
          border-color: #93401D;
          box-shadow: 0 0 0 4px rgba(181,85,59,0.12);
        }
      `}</style>
    </section>
  );
}

function Field({ label, htmlFor, children, className = "" }) {
  return (
    <div className={className}>
      <label
        htmlFor={htmlFor}
        className="block text-[11px] tracking-[0.2em] uppercase text-[#93401D] font-medium mb-2"
      >
        {label}
      </label>
      {children}
    </div>
  );
}

function ContactRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-4">
      <div className="w-11 h-11 rounded-2xl bg-[#EAD0AE] flex items-center justify-center text-[#93401D] shrink-0">
        <Icon size={18} strokeWidth={1.5} />
      </div>
      <div>
        <div className="text-[11px] tracking-[0.2em] uppercase text-[#937353] mb-1">{label}</div>
        <div className="text-[#29170C]">{value}</div>
      </div>
    </div>
  );
}
